import React, { PureComponent } from 'react';
import styled from 'styled-components';

import { getRequest } from '../../../../api/ApiService';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Wrapper = styled.div`
  background: #f5f6f7;
  flex: 1;
`;

const Container = styled.div`
  padding: 40px;
  width: 80%;
  max-width: 794px;
`;

const Text1 = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  color: rgba(0, 27, 59, 0.38);
  text-transform: uppercase;
`;

const Text2 = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  color: rgba(0, 27, 59, 0.94);
  margin-top: 24px;
`;

const Text3 = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 12px;

  b {
    color: #ed716d;

    &.black-note {
      color: rgba(0, 27, 59, 0.94);
    }
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d5d9dd;
  margin-top: 12px;
`;

class HowGradingWorksStep1 extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      chartData: [],
      colors: [],
    };
  }

  componentDidMount() {
    getRequest(`${process.env.REACT_APP_API_URL}v2/games/gradingweights/`)
      .then(res => {
        const data = res.body.results[0];
        const chartData = [];
        const colors = [];
        const peerScore = data ? Number(data.peer_weight) * 100 : 0;
        if (peerScore > 0) {
          chartData.push({
            name: 'Peer Score',
            value: peerScore,
          });
          colors.push('#52c5cd');
        }
        const teamScore = data ? Number(data.team_grade) * 100 : 0;
        if (teamScore > 0) {
          chartData.push({
            name: 'Team Score',
            value: teamScore,
          });
          colors.push('#ec5e59');
        }
        const engagementScore = data ? Number(data.engagement_score) * 100 : 0;
        if (engagementScore > 0) {
          chartData.push({
            name: 'Engagement Score',
            value: engagementScore,
          });
          colors.push('#f9c151');
        }

        this.setState({
          chartData: chartData.map(i => ({
            ...i,
            value: Number(i.value.toFixed(2)),
          })),
          colors,
        });
      })
      .catch(err => { });
  }

  renderLabel = (props) => {
    const {
      cx, cy, midAngle, outerRadius, value, name, index,
    } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 35;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    const images = {
      'Peer Score': 'onboarding-icon2',
      'Team Score': 'onboarding-icon1',
      'Engagement Score': 'onboarding-icon3',
    };

    return (
      <g>
        {index === 0 && (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            className="onboardingChartCenterLabel"
          >Individual</text>
        )}
        {index === 0 && (
          <text
            x={cx}
            y={cy}
            dy={20}
            textAnchor="middle"
            className="onboardingChartCenterLabel"
          >Score</text>
        )}
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="rgba(0, 27, 59, 0.94)" fill="none" />
        <image x={ex + (cos >= 0 ? 1 : -3) * 10} y={ey - 10} width="20" height="20" href={`/${images[name]}.svg`} />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 40}
          y={ey}
          dy={5}
          textAnchor={textAnchor}
          className="onboardingChartLabel"
        >{`${value}%`}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 40}
          y={ey}
          dy={27}
          textAnchor={textAnchor}
          className="onboardingChartDescription"
        >{name}</text>
      </g>
    );
  }

  render() {
    const { chartData, colors } = this.state;

    return (
      <Wrapper>
        <Container>
          <Text1>How Grading Works</Text1>
          <Text2>How Grading Works</Text2>
          <Text3>
            Below are the 3 factors that drive each student's overall score: <b>team score</b>,
            {' '}<b>peer score</b>, and <b>engagement score</b>. Financial targets for the team score
            as well as adjusting grading weights are the items that you can modify. Let's walk
            through each of these grading factors to explain.
          </Text3>

          {chartData.length > 0 &&
            <ChartWrapper>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    label={this.renderLabel}
                    labelLine={false}
                    startAngle={30}
                    endAngle={390}
                  >
                    ${colors.map((color, index) => (
                      <Cell key={`pie-cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </ChartWrapper>}

          <Text3>
            <b className="black-note">Note:</b> The <b>Game Admin</b> section is where you'll go to modify
            and review grading metrics.
          </Text3>
        </Container>
      </Wrapper>
    );
  }
}

export default HowGradingWorksStep1;



// WEBPACK FOOTER //
// ./src/instructor/components/onboarding/how-grading-works/step1.js